/* eslint-disable */
import React, { useEffect, useState } from 'react';
import {
  Box,
  VStack,
  Text,
  Flex,
  Heading,
  Divider,
  Switch,
  Button,
  HStack,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import axios from 'axios';
import { IMerchantSettings } from '@olo-web/types/merchantTheme.interface';
import { useMerchantTheme } from '@domain/merchants/queries/useMerchantTheme';
import { useMerchantGroup } from '@domain/merchants/queries';
import { createLocationString } from '@domain/merchants/functions/createLocationString';
import { useCustomThemeDraftDispatch, useCustomThemeDraftState } from '@olo-web/client-state';
import { useError, useToast } from '@olo-web/utils/common/hooks';
import { useUpdateMerchantTheme } from '@domain/merchants/mutations/useUpdateMerchantTheme';

export const MerchantSettings = () => {
  const { data: theme } = useMerchantTheme();
  const { data: group, isLoading: groupLoading } = useMerchantGroup();
  const { settings } = useCustomThemeDraftState();
  const themeDispatch = useCustomThemeDraftDispatch();
  const callError = useError();
  const { notify } = useToast();
  const [isAppEnabledLocations, setIsAppEnabledLocations] = useState<string[]>([]);
  const [loadingLocations, setLoadingLocations] = useState<Record<string, boolean>>({});
  const [initialLoading, setInitialLoading] = useState(true);
  const [isBulkLoading, setIsBulkLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();
  const [bulkAction, setBulkAction] = useState<'enable' | 'disable'>('enable');

  const { mutate: updateTheme, isLoading: isUpdatingTheme } = useUpdateMerchantTheme({
    onSuccess: () => {
      notify({
        status: 'success',
        title: 'Settings updated',
        description: 'Your Picked For You settings have been saved',
        variant: 'left-accent',
        position: 'bottom-left',
        duration: 2000,
      });
    },
    onError: (error) => callError({ error, toastError: true, title: 'Unable to save settings' }),
  });

  const updateDraftSettings = (data: IMerchantSettings) => {
    themeDispatch({
      type: 'UPDATE_MERCHANT_SETTINGS',
      payload: data,
    });
  };

  const handleRecommendationsToggle = (isChecked: boolean) => {
    if (!theme?.id) return;

    updateDraftSettings({
      ...settings,
      areRecommendationsEnabled: isChecked,
    });

    updateTheme({
      theme: {
        merchantIds: theme.merchantIds,
        settings: {
          ...settings,
          areRecommendationsEnabled: isChecked,
        },
      },
    });
  };

  useEffect(() => {
    const fetchInitialStatus = async () => {
      if (!group?.merchants?.length) {
        return;
      }

      try {
        const enabledLocations = await Promise.all(
          group.merchants.map(async (merchant) => {
            const { data } = await axios.get(
              `/api/digital-ordering/locations/${merchant.merchantId}`
            );
            return data.isAppEnabled ? merchant.merchantId : null;
          })
        );

        setIsAppEnabledLocations(enabledLocations.filter(Boolean));
      } catch (error) {
        callError({ error, toastError: true });
      } finally {
        setInitialLoading(false);
      }
    };

    fetchInitialStatus();
  }, [group?.merchants]);

  useEffect(() => {
    if (settings?.isAppEnabledLocations) {
      setIsAppEnabledLocations(settings.isAppEnabledLocations);
    } else {
      setIsAppEnabledLocations([]);
    }
  }, [settings?.isAppEnabledLocations]);

  const handleIsAppEnabledChange = async (merchantId: string, isChecked: boolean) => {
    setLoadingLocations((prev) => ({ ...prev, [merchantId]: true }));

    try {
      // nextjs api route
      await axios.patch(`/api/digital-ordering/locations/${merchantId}`, {
        isAppEnabled: isChecked,
      });

      const updatedLocations = isChecked
        ? [...isAppEnabledLocations, merchantId]
        : isAppEnabledLocations.filter((id) => id !== merchantId);

      setIsAppEnabledLocations(updatedLocations);

      notify({
        status: 'success',
        title: `GoTo ${isChecked ? 'enabled' : 'disabled'}`,
        description: `Location is now ${isChecked ? 'visible' : 'hidden'} in the GoTo app`,
        variant: 'left-accent',
        position: 'bottom-left',
        duration: 2000,
      });
    } catch (error) {
      callError({ error, toastError: true });
    } finally {
      setLoadingLocations((prev) => ({ ...prev, [merchantId]: false }));
    }
  };

  const updateLocationStatus = async (merchantId: string, isEnabled: boolean) => {
    try {
      await axios.patch(`/api/digital-ordering/locations/${merchantId}`, {
        isAppEnabled: isEnabled,
      });
      return true;
    } catch (error) {
      callError({ error, toastError: true });
      return false;
    }
  };

  const handleBulkToggle = async () => {
    if (!group?.merchants?.length) return;

    const isEnabling = bulkAction === 'enable';
    setIsBulkLoading(true);
    onClose();

    const newLoadingState = { ...loadingLocations };
    group.merchants.forEach((merchant) => {
      newLoadingState[merchant.merchantId] = true;
    });
    setLoadingLocations(newLoadingState);

    try {
      const locationsToUpdate = group.merchants
        .filter((merchant) => {
          const isCurrentlyEnabled = isAppEnabledLocations.includes(merchant.merchantId);
          return isEnabling ? !isCurrentlyEnabled : isCurrentlyEnabled;
        })
        .map((merchant) => merchant.merchantId);

      if (locationsToUpdate.length === 0) {
        notify({
          status: 'info',
          title: 'No changes needed',
          description: `All locations are already ${isEnabling ? 'enabled' : 'disabled'} for GoTo`,
          variant: 'left-accent',
          position: 'bottom-left',
          duration: 3000,
        });
        return;
      }

      const results = await Promise.all(
        locationsToUpdate.map(async (merchantId) => {
          const success = await updateLocationStatus(merchantId, isEnabling);
          return { merchantId, success };
        })
      );

      setIsAppEnabledLocations((prevLocations) => {
        let newLocations = [...prevLocations];

        if (isEnabling) {
          results.forEach((result) => {
            if (result.success && !newLocations.includes(result.merchantId)) {
              newLocations.push(result.merchantId);
            }
          });
        } else {
          results.forEach((result) => {
            if (result.success) {
              newLocations = newLocations.filter((id) => id !== result.merchantId);
            }
          });
        }

        return newLocations;
      });

      const successCount = results.filter((r) => r.success).length;

      notify({
        status: 'success',
        title: `Bulk operation complete`,
        description: `Successfully ${isEnabling ? 'enabled' : 'disabled'} ${successCount} of ${
          locationsToUpdate.length
        } locations`,
        variant: 'left-accent',
        position: 'bottom-left',
        duration: 3000,
      });
    } catch (error) {
      console.error('Bulk toggle error:', error);
      callError({ error, toastError: true });
    } finally {
      const clearedLoadingState = { ...loadingLocations };
      group.merchants.forEach((merchant) => {
        clearedLoadingState[merchant.merchantId] = false;
      });
      setLoadingLocations(clearedLoadingState);
      setIsBulkLoading(false);
    }
  };

  const openBulkDialog = (action: 'enable' | 'disable') => {
    setBulkAction(action);
    onOpen();
  };

  const isRecommendationsDataReady = Boolean(theme?.id);

  useEffect(() => {
    if (!settings && theme?.settings) {
      updateDraftSettings(theme.settings);
    }
    // intentionally leaving `updateDraftSettings` out of the dependency array
  }, [theme?.settings, settings]);

  return (
    <VStack w="100%" spacing={10} align="flex-start">
      <Text>Configure merchant settings</Text>

      <Box w="100%" p={5} borderWidth="1px" borderRadius="md" bg="gray.50">
        <VStack align="flex-start" spacing={4} w="100%">
          <Flex w="100%" justifyContent="space-between" alignItems="center">
            <Heading as="h3" size="md" color="primary.600">
              Picked For You
            </Heading>
          </Flex>
          <Divider borderColor="gray.300" />
          <Text fontSize="sm" color="gray.600">
            Enable personalized recommendations
          </Text>

          <Flex
            w="100%"
            justifyContent="space-between"
            alignItems="center"
            p={2}
            borderRadius="md"
            _hover={{ bg: 'gray.100' }}
          >
            <Flex flexDir="column">
              <Text textStyle="h3">Enable Picked For You</Text>
            </Flex>
            <Switch
              size="lg"
              colorScheme="primary"
              isChecked={settings?.areRecommendationsEnabled ?? false}
              onChange={(e) => handleRecommendationsToggle(e.target.checked)}
              isDisabled={isUpdatingTheme || !isRecommendationsDataReady}
            />
          </Flex>
        </VStack>
      </Box>

      <Box w="100%" p={5} borderWidth="1px" borderRadius="md" bg="gray.50">
        <VStack align="flex-start" spacing={4} w="100%">
          <Flex w="100%" justifyContent="space-between" alignItems="center">
            <Heading as="h3" size="md" color="primary.600">
              GoTo Settings
            </Heading>
            <HStack spacing={2}>
              <Button
                size="sm"
                colorScheme="primary"
                variant="outline"
                onClick={() => openBulkDialog('enable')}
                isDisabled={
                  initialLoading ||
                  isBulkLoading ||
                  (group?.merchants?.length > 0 &&
                    isAppEnabledLocations.length >= group.merchants.length)
                }
                title={
                  group?.merchants?.length > 0 &&
                  isAppEnabledLocations.length >= group.merchants.length
                    ? 'All locations are already enabled'
                    : ''
                }
              >
                Enable All
              </Button>
              <Button
                size="sm"
                colorScheme="primary"
                variant="outline"
                onClick={() => openBulkDialog('disable')}
                isDisabled={initialLoading || isBulkLoading || isAppEnabledLocations.length === 0}
                title={isAppEnabledLocations.length === 0 ? 'No locations are enabled' : ''}
              >
                Disable All
              </Button>
            </HStack>
          </Flex>
          <Divider borderColor="gray.300" />
          <Text fontSize="sm" color="gray.600">
            Control which locations are visible in the GoTo app
          </Text>

          <VStack w="100%" spacing={5} align="flex-start" mt={2}>
            {initialLoading ? (
              <Text>Loading locations...</Text>
            ) : isBulkLoading ? (
              <Text>Updating all locations...</Text>
            ) : (
              group?.merchants?.map((merchant) => (
                <Flex
                  key={merchant.merchantId}
                  w="100%"
                  justifyContent="space-between"
                  alignItems="center"
                  p={2}
                  borderRadius="md"
                  _hover={{ bg: 'gray.100' }}
                >
                  <Flex flexDir="column">
                    <Text textStyle="h3">{merchant.name}</Text>
                    <Text textStyle="subtitle1" fontWeight="400" color="gray.600">
                      {createLocationString(merchant)}
                    </Text>
                  </Flex>
                  <Switch
                    size="lg"
                    colorScheme="primary"
                    isChecked={isAppEnabledLocations.includes(merchant.merchantId)}
                    onChange={(e) =>
                      handleIsAppEnabledChange(merchant.merchantId, e.target.checked)
                    }
                    isDisabled={loadingLocations[merchant.merchantId] || isBulkLoading}
                  />
                </Flex>
              ))
            )}
          </VStack>
        </VStack>
      </Box>

      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {bulkAction === 'enable' ? 'Enable' : 'Disable'} All Locations
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to {bulkAction === 'enable' ? 'enable' : 'disable'} GoTo for all
              locations? This will make {bulkAction === 'enable' ? 'all' : 'no'} locations visible
              in the GoTo app.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme={bulkAction === 'enable' ? 'primary' : 'red'}
                onClick={handleBulkToggle}
                ml={3}
              >
                Confirm
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  );
};
